import { AiHelpArticle, AiHelpImage } from "@domain/ai_help"
import useTranslate from "@hooks/use_translate"
import { Box, Divider, Drawer, Stack, Typography } from "@mui/material"

export function HelpImage({ image }: { image: AiHelpImage }) {
  return (
    <Stack gap={0.5}>
      <Box
        component="img"
        src={image.url}
        alt={image.caption ?? ""}
        sx={{
          maxWidth: "100%",
          border: 1,
          borderColor: "divider",
          borderRadius: 1
        }}
      />
      {image.caption && (
        <Typography variant="caption" fontStyle="italic">
          {image.caption}
        </Typography>
      )}
      {image.description && (
        <Typography variant="caption" color="text.secondary">
          {image.description}
        </Typography>
      )}
    </Stack>
  )
}

/** The article as the Operator reads it: sections, screenshots with their descriptions, FAQ. */
export default function ArticleDrawer({
  article,
  onClose
}: {
  article: AiHelpArticle | null
  onClose: () => void
}) {
  const { translate } = useTranslate()

  return (
    <Drawer anchor="right" open={!!article} onClose={onClose}>
      {article && (
        <Stack gap={2} p={3} width={{ xs: "100vw", md: 720 }}>
          <Stack gap={0.5}>
            <Typography variant="h6">{article.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[article.slug, article.author, article.fileName]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            {article.summary && (
              <Typography variant="body2">{article.summary}</Typography>
            )}
          </Stack>
          {article.sections.map((section) => (
            <Stack key={section.key} gap={1}>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                {section.heading}
              </Typography>
              <Typography variant="body2" whiteSpace="pre-wrap">
                {section.text}
              </Typography>
              {section.images.map((image) => (
                <HelpImage key={image.key} image={image} />
              ))}
            </Stack>
          ))}
          {article.faqs.length > 0 && (
            <Stack gap={1}>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                {translate("ai_help_faqs")}
              </Typography>
              {article.faqs.map((faq) => (
                <Stack key={faq.question}>
                  <Typography variant="body2" fontWeight={600}>
                    {faq.question}
                  </Typography>
                  <Typography variant="body2">{faq.answer}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  )
}
